import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

// Types
interface User {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  role: string;
  entite_id?: number;
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

// Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Configuration Axios
const API_BASE_URL =
  process.env.REACT_APP_API_URL ?? (typeof window !== 'undefined' ? '' : 'http://localhost:5001');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token à chaque requête
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('comptaEBNL_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('comptaEBNL_token');
      localStorage.removeItem('comptaEBNL_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Provider
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const savedToken = localStorage.getItem('comptaEBNL_token');
        const savedUser = localStorage.getItem('comptaEBNL_user');

        if (savedToken && savedUser) {
          setToken(savedToken);
          setUser(JSON.parse(savedUser));
          
          // Vérifier que le token est toujours valide
          try {
            await apiClient.get('/api/v1/auth/me');
          } catch (error) {
            // Token invalide, déconnexion
            logout();
          }
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'authentification:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Fonction de connexion
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      const response = await apiClient.post('/api/v1/auth/login', {
        nom_utilisateur: email,
        mot_de_passe: password,
      });

      if (response.data.success) {
        const { token: newToken, utilisateur } = response.data.data;
        
        // Sauvegarder en localStorage
        localStorage.setItem('comptaEBNL_token', newToken);
        localStorage.setItem('comptaEBNL_user', JSON.stringify(utilisateur));
        
        // Mettre à jour l'état
        setToken(newToken);
        setUser(utilisateur);
        
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    localStorage.removeItem('comptaEBNL_token');
    localStorage.removeItem('comptaEBNL_user');
    setToken(null);
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    logout,
    isAuthenticated: !!token && !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook pour utiliser le context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

// Export de l'instance axios configurée
export { apiClient };
export default AuthContext;